/**
 * SearchSpring Shared Client + Card Renderer
 *
 * IIFE defer script providing:
 * - SSClient: API client with retry logic and identity management
 * - createSSClient: Singleton factory for SSClient instances
 * - Card renderer: HTML builder matching native Liquid card DOM structure
 *
 * Shared by both collection pages (ss-collection.js) and search pages (ss-search.js).
 * Loaded as defer script so it executes before type="module" orchestrators.
 */

(function () {
  'use strict';

  // ===========================================================================
  // SS API Client
  // ===========================================================================

  const USER_ID_KEY = 'ss-userId';
  const SESSION_ID_KEY = 'ss-sessionId';

  class SSClient {
    #siteId;
    #timeout;
    #maxRetries;
    #baseUrl;
    #pageLoadId;

    constructor({ siteId, timeout = 5000, maxRetries = 1 }) {
      this.#siteId = siteId;
      this.#timeout = timeout;
      this.#maxRetries = maxRetries;
      this.#baseUrl = `https://${siteId}.a.searchspring.io/api/search/search.json`;
      this.#pageLoadId = crypto.randomUUID();

      // Ensure SS identity exists as soon as any SS module loads.
      // Beacon, recommendations, and other modules read these directly
      // from storage — they must exist before any tracking fires.
      this.#getOrCreateId(USER_ID_KEY, localStorage);
      this.#getOrCreateId(SESSION_ID_KEY, sessionStorage);
    }

    get siteId() {
      return this.#siteId;
    }

    get pageLoadId() {
      return this.#pageLoadId;
    }

    async search(params = {}) {
      const url = new URL(this.#baseUrl);
      url.searchParams.set('siteId', this.#siteId);
      url.searchParams.set('resultsFormat', 'json');
      url.searchParams.set('userId', this.#getOrCreateId(USER_ID_KEY, localStorage));
      url.searchParams.set('sessionId', this.#getOrCreateId(SESSION_ID_KEY, sessionStorage));
      url.searchParams.set('pageLoadId', this.#pageLoadId);
      url.searchParams.set('domain', window.location.href);
      url.searchParams.set('beacon', 'true');

      for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
          value.forEach((v) => url.searchParams.append(key, v));
        } else {
          url.searchParams.set(key, value);
        }
      }

      return this.#fetchWithRetry(url);
    }

    async #fetchWithRetry(url) {
      for (let attempt = 0; attempt <= this.#maxRetries; attempt++) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), this.#timeout);

        try {
          const response = await fetch(url, { signal: controller.signal });
          clearTimeout(timer);

          if (!response.ok) {
            throw new Error(`SS API ${response.status} ${response.statusText}`);
          }

          return await response.json();
        } catch (error) {
          clearTimeout(timer);
          console.error('[SS]', error);
        }
      }

      return null;
    }

    #getOrCreateId(key, storage) {
      let id = storage.getItem(key);
      if (!id) {
        id = crypto.randomUUID();
        storage.setItem(key, id);
      }
      return id;
    }
  }

  let clientInstance = null;

  function createSSClient(config) {
    if (!clientInstance) {
      clientInstance = new SSClient(config);
    }
    return clientInstance;
  }

  // ===========================================================================
  // Card Renderer
  // ===========================================================================

  function formatMoney(cents) {
    const format =
      window.Shopify?.money_format || window.theme?.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.floor(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',');

    let result = format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace(
        '{{amount_no_decimals_with_comma_separator}}',
        amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      )
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);

    if (window.Theme?.settings?.removeTrailingZeros) {
      result = result.replace(/\.00(?=\s|$)/, '');
    }

    return result;
  }

  function buildSrcset(url) {
    if (!url) return '';

    const widths = [375, 770, 1100];

    if (url.includes('cdn.shopify.com')) {
      const base = url.split('?')[0];
      return widths.map((w) => `${base}?width=${w} ${w}w`).join(', ');
    }

    const sizePattern = /_\d+x\d*\./;
    if (sizePattern.test(url)) {
      return widths
        .map((w) => `${url.replace(sizePattern, `_${w}x.`)} ${w}w`)
        .join(', ');
    }

    return `${url} 770w`;
  }

  function toCents(dollars) {
    const num = parseFloat(dollars);
    return isNaN(num) ? 0 : Math.round(num * 100);
  }

  /**
   * Determine product availability from SS data.
   * SS returns ss_available as "1"/"0" string, not a boolean.
   */
  function isAvailable(product) {
    if (product.ss_available !== undefined) return String(product.ss_available) === '1';
    if (product.available !== undefined) return product.available !== false;
    return true;
  }

  /**
   * Resolve sale pricing for a product, mirroring snippets/price.liquid:
   * prefer a real compare-at price (product.msrp); if none, fall back to
   * the Hanro discount_percent:<n> / discount_amount:<n> product tags,
   * synthesizing a compare-at price from the current price. Requires
   * product.tags to be present in the Athos feed (same field renderLabels()
   * already depends on for the finalsale tag).
   */
  function resolveSalePricing(product) {
    let priceCents = toCents(product.price);
    let compareCents = toCents(product.msrp);
    let savePercent = 0;
    let isOnSale = compareCents > priceCents;

    if (isOnSale) {
      savePercent = Math.floor(((compareCents - priceCents) * 100) / compareCents);
    } else if (Array.isArray(product.tags)) {
      for (const tag of product.tags) {
        if (typeof tag !== 'string') continue;

        if (tag.indexOf('discount_percent:') !== -1) {
          const pct = parseInt(tag.split(':').pop(), 10) || 0;
          if (pct > 0) {
            compareCents = priceCents;
            priceCents = Math.floor((compareCents * (100 - pct)) / 100);
            savePercent = pct;
            isOnSale = true;
          }
          break;
        } else if (tag.indexOf('discount_amount:') !== -1) {
          const dollars = parseFloat(tag.split(':').pop()) || 0;
          if (dollars > 0) {
            compareCents = priceCents;
            const discountCents = Math.round(dollars * 100);
            priceCents = compareCents - discountCents;
            savePercent = Math.floor((discountCents * 100) / compareCents);
            isOnSale = true;
          }
          break;
        }
      }
    }

    return { priceCents, compareCents, isOnSale, savePercent };
  }

  function renderPrice(product) {
    const { priceCents, compareCents, isOnSale, savePercent } = resolveSalePricing(product);

    if (!priceCents) return '';

    const formatted = formatMoney(priceCents);

    if (isOnSale && isAvailable(product)) {
      const formattedCompare = formatMoney(compareCents);
      return (
        '<span role="group"><span class="price">' + formatted + '</span></span>' +
        '<span role="group"><span class="compare-at-price">' + formattedCompare + '</span></span>' +
        (savePercent > 0 ? '<span class="price-save-percent">Save ' + savePercent + '%</span>' : '')
      );
    }

    return '<span class="price">' + formatted + '</span>';
  }

  function renderSwatches(product, siblings) {
    if (!siblings || !Array.isArray(siblings) || siblings.length <= 1) return '';

    const buttons = siblings
      .map((sib) => {
        const isActive = sib.handle === product.handle;
        const swatchUrl = sib.swatch_image || '';
        const srcset = buildSrcset(sib.image_url);
        const priceCents = toCents(sib.price);
        const compareCents = toCents(sib.compare_at_price);

        const visualClass = swatchUrl
          ? 'product-card-swatch__visual'
          : 'product-card-swatch__visual product-card-swatch__visual--placeholder';
        const visualStyle = swatchUrl
          ? ` style="background-image: url('${escapeAttr(swatchUrl)}');"`
          : '';

        return (
          `<button type="button"` +
          ` class="product-card-swatch${isActive ? ' active' : ''}"` +
          ` data-swatch` +
          ` data-srcset="${escapeAttr(srcset)}"` +
          ` data-product-url="${escapeAttr(sib.url || '')}"` +
          ` data-product-handle="${escapeAttr(sib.handle || '')}"` +
          ` data-product-title="${escapeAttr(sib.title || '')}"` +
          ` data-product-price="${priceCents}"` +
          ` data-compare-at-price="${compareCents}"` +
          ` data-available="${sib.available !== false}"` +
          ` aria-label="${escapeAttr(sib.title || '')}"` +
          `${isActive ? ' aria-current="true"' : ''}>` +
          `<span class="${visualClass}"${visualStyle}></span>` +
          `<span class="visually-hidden">${escapeHtml(sib.title || '')}</span>` +
          `</button>`
        );
      })
      .join('');

    return (
      '<hanro-product-swatches class="product-card-swatches">' +
      '<fieldset class="product-card-swatches__list">' +
      '<legend class="visually-hidden">Color</legend>' +
      buttons +
      '</fieldset>' +
      '</hanro-product-swatches>'
    );
  }

  function renderLabels(product) {
    const labels = [];

    for (let i = 1; i <= 4; i++) {
      const value = product[`mfield_custom_product_label_${i}`];
      if (value) {
        labels.push(`<span class="hanro-product-label">${escapeHtml(value)}</span>`);
      }
    }

    if (Array.isArray(product.tags)) {
      const hasFinalsale = product.tags.some(
        (t) => t === 'finalsale' || t === 'Finalsale'
      );
      if (hasFinalsale) {
        labels.push(
          '<span class="hanro-product-label hanro-product-label--final-sale">Final Sale</span>'
        );
      }
    }

    if (labels.length === 0) return '';
    return '<div class="hanro-product-labels">' + labels.join('') + '</div>';
  }

  function renderBadge(product, config) {
    // Sale badges are intentionally not shown on Athos-rendered cards per
    // client feedback — the discounted price and "Save X%" text already
    // communicate the sale. Only the Sold Out badge and the separate
    // Final Sale label (see renderLabels) remain.
    const isSoldOut = !isAvailable(product);
    if (!isSoldOut) return '';

    const badge = config.badge || {};
    const badgeClass =
      'product-badges__badge product-badges__badge--rectangle color-' +
      (badge.soldOutScheme || 'scheme-5');
    const badgeText = badge.soldOutText || 'Sold out';

    const position = badge.position || 'top-left';
    const parsedCornerRadius = parseInt(badge.cornerRadius, 10);
    const cornerRadius = Number.isNaN(parsedCornerRadius) ? 40 : parsedCornerRadius;
    return (
      `<div class="product-badges product-badges--${escapeAttr(position)}" style="` +
      `--badge-border-radius: ${cornerRadius}px;` +
      `--badge-font-family: var(--font-${escapeAttr(badge.fontFamily || 'body')}--family);` +
      `--badge-font-weight: var(--font-${escapeAttr(badge.fontFamily || 'body')}--weight);` +
      `--badge-text-transform: ${escapeAttr(badge.textTransform || 'uppercase')};` +
      `">` +
      `<div class="${badgeClass}">${escapeHtml(badgeText)}</div>` +
      `</div>`
    );
  }

  // Athos returns mfield_reviews_rating as an HTML-entity-encoded JSON string,
  // e.g. '{&quot;scale_min&quot;:&quot;1.0&quot;,&quot;scale_max&quot;:&quot;5.0&quot;,&quot;value&quot;:&quot;4.71&quot;}'.
  // Returns { value, scaleMax } as numbers, or null when missing/invalid.
  function parseRating(product) {
    const raw = product.mfield_reviews_rating;
    if (!raw) return null;
    try {
      const decoded = decodeEntities(raw);
      const parsed = typeof decoded === 'string' ? JSON.parse(decoded) : decoded;
      const value = parseFloat(parsed && parsed.value);
      const scaleMax = parseInt(parsed && parsed.scale_max, 10) || 5;
      if (isNaN(value)) return null;
      return { value, scaleMax };
    } catch {
      return null;
    }
  }

  // Renders Klaviyo review stars matching blocks/review.liquid's output.
  // Skips render when rating is missing OR review count is 0 — Klaviyo's
  // sync sometimes emits a rating with zero reviews; treating that as no data.
  // No count text per HAN-354 (PLP/PDP/YMAL should show stars only).
  function renderReviewStars(product) {
    const rating = parseRating(product);
    if (!rating) return '';

    const countRaw = product.mfield_reviews_rating_count;
    const count = parseInt(String(countRaw || '0'), 10);
    if (!count || count <= 0) return '';

    // Half-star math — direct port of blocks/review.liquid:18-31.
    const decimal = rating.value % 1;
    let decimalRating = 0;
    if (decimal >= 0.3 && decimal <= 0.7) decimalRating = 0.5;
    else if (decimal > 0.7) decimalRating = 1;
    const decimalRounded = Math.ceil(decimalRating);
    const filledStars = Math.floor(rating.value) + decimalRounded;

    // SVG defs (#half gradient + #star symbol) are emitted per card to mirror
    // native review.liquid. Duplicate IDs across cards are intentional —
    // native PLP behaves the same and browsers resolve <use> against the
    // nearest in-tree definition.
    const defsSVG =
      '<svg class="visually-hidden" style="width: 0; height: 0;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" role="presentation">' +
      '<defs>' +
      '<linearGradient id="half" x1="0" x2="100%" y1="0" y2="0">' +
      '<stop offset="50%" stop-color="var(--star-fill-color)"></stop>' +
      '<stop offset="50%" stop-color="rgb(var(--star-fill-color-rgb) / var(--opacity-20))"></stop>' +
      '</linearGradient>' +
      '<symbol xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" id="star">' +
      '<path d="M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z"/>' +
      '</symbol>' +
      '</defs>' +
      '</svg>';

    let starsHTML = '';
    for (let i = 1; i <= rating.scaleMax; i++) {
      const filledClass = i <= filledStars ? ' filled-star' : '';
      const useFill = decimalRating === 0.5 && i === filledStars ? ' fill="url(#half)"' : '';
      starsHTML +=
        `<svg class="stars${filledClass}" viewBox="0 0 32 32" ` +
        `style="--empty-star-fill-color: rgb(var(--star-fill-color-rgb) / var(--opacity-20));" ` +
        `role="presentation">` +
        `<use xlink:href="#star"${useFill}></use>` +
        `</svg>`;
    }

    const ariaLabel = `Rated ${rating.value} out of ${rating.scaleMax}`;
    const starSize = 'calc(var(--font-paragraph--size) * 1.1)';

    return (
      `<div class="rating-wrapper rating-color--primary justify-left">` +
      defsSVG +
      `<div class="rating" style="--star-size: ${starSize};" aria-label="${escapeAttr(ariaLabel)}">` +
      starsHTML +
      `</div>` +
      `</div>`
    );
  }

  function parseSiblings(product) {
    if (!product.mfield_custom_siblings) return null;

    try {
      const parsed =
        typeof product.mfield_custom_siblings === 'string'
          ? JSON.parse(product.mfield_custom_siblings)
          : product.mfield_custom_siblings;
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  let metafieldWarningLogged = false;

  // Horizon's ProductCard.navigateToProduct (product-card.js) refuses to navigate
  // when the productCardLink anchor has no id — it doubles as the card's scroll
  // anchor. Counter suffix keeps ids unique when the same product renders in
  // multiple grids on one page (PLP + recommendations + autocomplete).
  let cardLinkIdCounter = 0;

  // Parse the SS `images` field (pipe-delimited string of full-size URLs)
  // into an array, falling back to the singular `image`/`imageUrl` when absent.
  // images[0] always equals image; treating images as the authoritative list.
  function parseImageList(product) {
    const raw = product.images;
    if (typeof raw === 'string' && raw.length > 0) {
      const list = raw.split('|').filter(Boolean);
      if (list.length > 0) return list;
    }
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.filter(Boolean);
    }
    const single = product.image || product.imageUrl || product.thumbnailImageUrl || '';
    return single ? [single] : [];
  }

  // Match Horizon's card-gallery.liquid perf cap: 5 slides when carousel is on,
  // 2 when only hover is on, 1 otherwise. Keeps DOM size sane on PLPs.
  function maxVisibleSlides(config) {
    if (config.productCardCarousel) return 5;
    if (config.showSecondImageOnHover) return 2;
    return 1;
  }

  // SVG paths kept inline so the renderer has no asset dependency at runtime.
  // Match Horizon's icon-arrow.svg / icon-caret.svg exactly.
  const ARROW_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>';
  const CARET_SVG = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // Emit a single slideshow arrow button matching snippets/slideshow-arrow.liquid output.
  function buildSlideshowArrow(action, iconStyle, iconShape) {
    const style = iconStyle || 'arrow';
    const shape = iconShape || 'none';
    const iconName = style.includes('chevron') ? 'caret' : 'arrow';
    const iconSize = style.includes('large') ? 'large' : null;
    const svgHTML = iconName === 'caret' ? CARET_SVG : ARROW_SVG;

    const classes = [
      'slideshow-control',
      `slideshow-control--${action}`,
      iconSize ? `slideshow-control--${iconSize}` : null,
      shape !== 'none' ? `slideshow-control--shape-${shape}` : null,
      `slideshow-control--style-${style}`,
      'button', 'button-unstyled', 'button-unstyled--transparent',
      action === 'previous' ? 'flip-x' : null,
    ].filter(Boolean).join(' ');

    const label = action === 'previous' ? 'Previous' : 'Next';

    return (
      `<button class="${classes}" aria-label="${label}" on:click="/${action}" ref="${action}">` +
      `<span class="svg-wrapper icon-${iconName}">${svgHTML}</span>` +
      `</button>`
    );
  }

  // Emit the <slideshow-arrows> block. Matches snippets/slideshow-arrows.liquid.
  // Default position="center" — matches card-gallery.liquid which passes no override.
  function buildSlideshowArrows(iconStyle, iconShape) {
    return (
      `<slideshow-arrows position="center">` +
      buildSlideshowArrow('previous', iconStyle, iconShape) +
      buildSlideshowArrow('next', iconStyle, iconShape) +
      `</slideshow-arrows>`
    );
  }

  function renderGallerySingleImage(imageUrl, name, badgeHTML, url, ratio) {
    const srcset = buildSrcset(imageUrl);
    return (
      `<div ref="cardGallery" class="card-gallery" style="--gallery-aspect-ratio: ${escapeAttr(ratio)};">` +
      badgeHTML +
      `<a class="contents" ref="cardGalleryLink" href="${escapeAttr(url)}" aria-label="${escapeAttr(name)}">` +
      (imageUrl
        ? `<img class="product-media__image" src="${escapeAttr(imageUrl)}" srcset="${escapeAttr(srcset)}" sizes="(min-width: 750px) 50vw, 100vw" loading="lazy" alt="${escapeAttr(name)}" width="770" height="1027">`
        : '') +
      `</a>` +
      `</div>`
    );
  }

  function renderGallerySlideshow(images, name, badgeHTML, url, ratio, shopifyId, config) {
    const slides = images
      .map((src, i) => {
        const srcset = buildSrcset(src);
        const loading = i === 0 ? 'eager' : 'lazy';
        return (
          `<slideshow-slide ref="slides[]" ` +
          `aria-hidden="${i === 0 ? 'false' : 'true'}" ` +
          `class="product-media-container media-fit" ` +
          `slide-id="${escapeAttr(shopifyId)}-${i}" ` +
          `style="--slideshow-timeline: --slide-${i}; --product-media-fit: cover;">` +
          `<img class="product-media__image" src="${escapeAttr(src)}" srcset="${escapeAttr(srcset)}" ` +
          `sizes="(min-width: 750px) 50vw, 100vw" loading="${loading}" alt="${escapeAttr(name)}" ` +
          `width="770" height="1027">` +
          `</slideshow-slide>`
        );
      })
      .join('');

    const timelineScope = images.map((_, i) => `--slide-${i}`).join(', ');
    const disabledAttr = config.productCardCarousel === false ? ' disabled="true"' : '';

    const hoverAttrs = config.showSecondImageOnHover && images.length > 1
      ? ' on:pointerenter="/previewImage" on:pointerleave="/resetImage"'
      : '';

    // Match Horizon: arrows show when there are more than 2 images. They're hidden
    // on mobile via product-card.liquid's existing CSS rule (display: none < 750px).
    const arrowsHTML = images.length > 2
      ? buildSlideshowArrows(config.productCardIconsStyle, null)
      : '';

    return (
      `<div ref="cardGallery" class="card-gallery" ` +
      `style="--gallery-aspect-ratio: ${escapeAttr(ratio)};" ` +
      `data-product-id="${escapeAttr(shopifyId)}"${hoverAttrs}>` +
      badgeHTML +
      `<a class="contents" ref="cardGalleryLink" href="${escapeAttr(url)}" aria-label="${escapeAttr(name)}">` +
      `<slideshow-component ref="slideshow" ` +
      `style="--slideshow-timeline: ${timelineScope};" ` +
      `initial-slide="0" infinite${disabledAttr}>` +
      `<slideshow-container ref="slideshowContainer">` +
      arrowsHTML +
      `<slideshow-slides tabindex="-1" ref="scroller">` +
      slides +
      `</slideshow-slides>` +
      `</slideshow-container>` +
      `</slideshow-component>` +
      `</a>` +
      // HAN-378: image-count indicators. Built by hanro-image-indicators.js from the
      // slideshow's visible slides; this path only runs for 2+ images.
      `<hanro-image-indicators class="hanro-image-indicators" aria-hidden="true"></hanro-image-indicators>` +
      `</div>`
    );
  }

  function renderProductCard(product, config = {}) {
    if (!product) return '';

    const shopifyId = product.uid || product.id || '';
    const url = product.url || `/products/${product.handle || ''}`;
    const name = product.name || product.title || '';
    const imageList = parseImageList(product);
    const primaryImage = imageList[0] || '';

    const collectionName = product.mfield_custom_collection_name || '';
    const styleName = product.mfield_custom_style_name || name;

    if (!metafieldWarningLogged && (!product.mfield_custom_collection_name || !product.mfield_custom_style_name)) {
      console.warn('[SS] Missing collection_name/style_name metafields — verify Athos indexing config. Product:', product.handle || shopifyId);
      metafieldWarningLogged = true;
    }

    const siblings = parseSiblings(product);
    const swatchesHTML = renderSwatches(product, siblings);
    const priceHTML = renderPrice(product);
    const reviewStarsHTML = renderReviewStars(product);
    const labelsHTML = renderLabels(product);
    const badgeHTML = renderBadge(product, config);

    const ratio = config.galleryAspectRatio || '1';
    const cap = maxVisibleSlides(config);
    const visibleImages = imageList.slice(0, cap);

    // singleImage opt-out (autocomplete) or only one image available — render simple gallery.
    // Otherwise emit the slideshow structure that mirrors Horizon's card-gallery.liquid.
    const galleryHTML = (config.singleImage || visibleImages.length <= 1)
      ? renderGallerySingleImage(primaryImage, name, badgeHTML, url, ratio)
      : renderGallerySlideshow(visibleImages, name, badgeHTML, url, ratio, shopifyId, config);

    const collectionNameHTML = collectionName
      ? `<p class="text-block text-block--align-left custom-typography custom-font-size custom-color" style="margin: 0; --font: var(--font-body--family); --font-size: 0.75rem; --line-height: normal; --letter-spacing: normal; --text-transform: uppercase; --width: fit-content; --custom-color: var(--color-foreground);">${escapeHtml(collectionName)}</p>`
      : '';

    const styleNameHTML =
      `<p class="text-block text-block--align-left rte" style="margin: 0; --font: var(--font-body--family); --font-size: 1rem; --line-height: normal; --letter-spacing: normal; --text-transform: none; --width: fit-content; --custom-color: var(--color-foreground);">${escapeHtml(styleName)}</p>`;

    const priceContainerHTML =
      `<product-price class="text-block text-block--align-left" style="--font: var(--font-body--family); --font-size: 1rem; --line-height: normal; --width: 100%;">` +
      `<div ref="priceContainer">${priceHTML}</div>` +
      `</product-price>`;

    const metaobjectGid = product.mfield_custom_link_to_products || '';

    return (
      `<product-card class="product-card" data-product-id="${escapeAttr(shopifyId)}"${metaobjectGid ? ` data-metaobject-gid="${escapeAttr(metaobjectGid)}"` : ''}>` +
      `<a id="ss-product-card-link-${escapeAttr(shopifyId)}-${++cardLinkIdCounter}" class="product-card__link" ref="productCardLink" href="${escapeAttr(url)}">` +
      `<span class="visually-hidden">${escapeHtml(name)}</span>` +
      `</a>` +
      `<div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style gap-style" style="--product-card-gap: 4px;">` +
      galleryHTML +
      collectionNameHTML +
      styleNameHTML +
      priceContainerHTML +
      reviewStarsHTML +
      labelsHTML +
      `</div>` +
      `</product-card>`
    );
  }

  function renderProductGrid(products, config = {}) {
    if (!Array.isArray(products)) return '';

    const tag = config.itemTag || 'li';
    const cls = config.itemClass || 'product-grid__item';
    const pageAttr = config.page ? ` data-page="${config.page}"` : '';
    const seenGids = config.seenGids instanceof Set ? config.seenGids : null;

    const parts = [];
    for (const product of products) {
      if (seenGids) {
        const gid = product.mfield_custom_link_to_products || '';
        if (gid) {
          if (seenGids.has(gid)) continue;
          seenGids.add(gid);
        }
      }
      parts.push(
        `<${tag} class="${cls}" data-product-id="${escapeAttr(product.uid || product.id || '')}"${pageAttr}>` +
          renderProductCard(product, config) +
          `</${tag}>`
      );
    }
    return parts.join('');
  }

  // --- Utility helpers ---

  // Athos returns HTML-encoded text; decode before re-escaping.
  function decodeEntities(str) {
    const txt = document.createElement('textarea');
    txt.innerHTML = String(str);
    return txt.value;
  }

  function escapeHtml(str) {
    if (!str) return '';
    return decodeEntities(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    if (!str) return '';
    return decodeEntities(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;');
  }

  // ===========================================================================
  // Exports
  // ===========================================================================

  window.SSClient = SSClient;
  window.createSSClient = createSSClient;
  window.renderProductCard = renderProductCard;
  window.renderProductGrid = renderProductGrid;
  window.formatMoney = formatMoney;
})();
